import type { FeatureFlagMeta } from '@gat-ship/feature-flag'

/**
 * Pure decisions for the Account panel's dev-info + feature-flag section:
 * dev-info visibility, which granted flags to surface, their metadata, and the
 * enabled/next-disabled processing. No `vue`/`ref`/IO; the shell owns the
 * `updateUserPreferences` write and resolves the env/profile reads.
 */

/** The `mail-prototype` flag is a credential pair rather than a boolean. */
export interface MailPrototypeValue {
  username: string | null
  password: string | null
}

/** A feature-flag value as carried by the flag client. */
export type FlagValue = boolean | number | string | MailPrototypeValue

/** Whether to show the developer info rows (user/tenant/database ids). */
export function isDevInfoVisible(
  env: { readonly isDev: boolean; readonly isStaging: boolean },
  email: string | undefined
): boolean {
  return (
    env.isDev || env.isStaging || (email?.endsWith('@gatship.com') ?? false)
  )
}

/**
 * Granted flags worth surfacing: registered, and truthy — except
 * `mail-prototype`, which requires both a username and a password.
 */
export function grantedFeatureFlags(
  allFlags: Readonly<Record<string, FlagValue>>,
  registry: Readonly<Record<string, FeatureFlagMeta>>
): { name: string }[] {
  const result: { name: string }[] = []
  for (const [name, value] of Object.entries(allFlags)) {
    if (!(name in registry)) continue
    if (name === 'mail-prototype') {
      const mailPrototypeValue = value as MailPrototypeValue
      if (mailPrototypeValue.username && mailPrototypeValue.password) {
        result.push({ name })
      }
    } else if (value) {
      result.push({ name })
    }
  }
  return result
}

/** Metadata for a flag, falling back to the raw name when unregistered. */
export function featureFlagMeta(
  name: string,
  registry: Readonly<Record<string, FeatureFlagMeta>>
): FeatureFlagMeta {
  if (name in registry) return registry[name]!
  return { title: name, description: '' }
}

/** A flag is enabled unless the user has explicitly opted out of it. */
export function isFeatureFlagEnabled(
  name: string,
  disabled: readonly string[]
): boolean {
  return !disabled.includes(name)
}

/** The next `disabledFeatureFlags` array after toggling one flag. */
export function nextDisabledFeatureFlags(
  name: string,
  enabled: boolean,
  current: readonly string[]
): string[] {
  return enabled ? current.filter((f) => f !== name) : [...current, name]
}
